import { formatNumberValue, UNIT_KW, UNIT_MW } from './utils';

/**
 * One renderer per `tooltipKey` (see useDataOrchestrator.js's resolveTooltipKey), i.e. per
 * distinct tooltip content — not per page/URL. Several pages intentionally share a renderer
 * (e.g. all "durch Verkehrswende" subcategories share `transportChange`).
 */
const TOOLTIP_RENDERERS = {
  electricVehicles: (values) => `
    ${values.electricVehicleRatio ? `<li>Anteil Elektroautos: ${values.electricVehicleRatio} %</li>` : ''}
    <li>Elektroautos: ${formatNumberValue(values.amount, 0)}</li>
    <li>CO₂-Einsparung/Jahr (E-Autos geladen mit Strommix): ${formatNumberValue(values.co2SavingsMixedElectricity, 0, 2)} t</li>
    <li>CO₂-Einsparung/Jahr (E-Autos geladen mit 100% Ökostrom): ${formatNumberValue(values.co2SavingsGreenElectricity, 0, 2)} t</li>
  `,
  bidiAtlas: (values) => `
    <li>Bidi-ready E-Autos: ${formatNumberValue(values.bidiReadyElectricVehicles, 0, 0)}</li>
    <li>Speicher-Potenzial in kWh: ${formatNumberValue(values.storageCapacityKWh, 0, 0)}</li>
    <li>Versorgbare Haushalte: ${formatNumberValue(values.supportedHouseholds, 0, 0)}</li>
  `,
  chargers: (values, region) => `
    <li>Ladepunkte: ${formatNumberValue(values.points, 0)}</li>
    <li>Ladestationen: ${formatNumberValue(values.stations, 0)}</li>
    <li>Gesamte Ladeleistung: ${region === 'districts' ? `${formatNumberValue(values.totalPowerRatingMW * 1000, 0, 0)} ${UNIT_KW}` : `${formatNumberValue(values.totalPowerRatingMW)} ${UNIT_MW}`}</li>
    <li>Ladeleistung pro E-Auto: ${formatNumberValue(values.totalPowerRatingKWPer1000)} ${UNIT_KW}</li>
  `,
  electricBicycles: (values, region) => region === 'states' ? `
    ${values.owners ? `<li>E-Bike-Besitzer: ${formatNumberValue(values.owners, 0)}%</li>` : ''}
    ${values.potential ? `<li>E-Bike Potential: ${formatNumberValue(values.potential, 0)}%</li>` : ''}
    ${values.greenEnergy ? `<li>Laden mit Ökostrom: ${formatNumberValue(values.greenEnergy, 0)}%</li>` : ''}
    ${values.highUsage ? `<li>Vielfahrer (≥ 200km/Mon.): ${formatNumberValue(values.highUsage, 0)}%</li>` : ''}
    ${values.flexReadiness ? `<li>Flex-Bereitschaft: ${formatNumberValue(values.flexReadiness, 0)}%</li>` : ''}
    ${values.flexAmount ? `<li>Flex-Menge: ${values.flexAmount} GWh</li>` : ''}
  ` : `<li>E-Bike-Besitzer: ${formatNumberValue(values.owners, 0)}%</li>`,
  electricTrucks: (values) => `
    <li>E-Lkw: ${formatNumberValue(values.amount, 0, 0)}</li>
    <li>CO₂-Einsparung/Jahr (E-Lkw geladen mit Strommix): ${formatNumberValue(values.co2SavingsMixedElectricity, 0, 0)} t</li>
    <li>CO₂-Einsparung/Jahr (E-Lkw geladen mit 100% Ökostrom): ${formatNumberValue(values.co2SavingsGreenElectricity, 0, 0)} t</li>
  `,
  co2Savings: (values) => `
    <li>Geplante CO₂-Einsparung/Jahr: ${formatNumberValue(values.co2SavingsPlanned, 0, 0)} t</li>
    <li>Energiewende Zuhause: ${formatNumberValue(values.energyChangeAtHome, 0, 0)} t</li>
    <li>Verkehrswende: ${formatNumberValue(values.transportChange, 0, 0)} t</li>
    <li>Verbrauchswende: ${formatNumberValue(values.consumptionChange, 0, 0)} t</li>
    <li>Max. CO₂-Sparpotenzial: ${formatNumberValue(values.generalCo2SavingsPotential, 0, 0)} t</li>
  `,
  energyChange_total: (values) => `
    <li>Geplante CO₂-Einsparung/Jahr: ${formatNumberValue(values.energyChangeAtHome, 0, 0)} t</li>
    <li>Dach-Solaranlage: ${formatNumberValue(values.installmentsRoofSolar, 0, 0)} t</li>
    <li>Balkon-Solaranlage: ${formatNumberValue(values.installmentsBalconySolar, 0, 0)} t</li>
    <li>Effiziente Heizanlage: ${formatNumberValue(values.efficientHeating, 0, 0)} t</li>
    <li>Max. CO₂-Sparpotenzial: ${formatNumberValue(values.homeCo2SavingsPotential, 0, 0)} t</li>
  `,
  energyChange: (values) => `
    <li>Geplante CO₂-Einsparung/Jahr: ${formatNumberValue(values.energyChangeAtHome, 0, 0)} t</li>
    <li>Dach-Solaranlage: ${formatNumberValue(values.installmentsRoofSolar, 0, 0)} t</li>
    <li>Balkon-Solaranlage: ${formatNumberValue(values.installmentsBalconySolar, 0, 0)} t</li>
    <li>Effiziente Heizanlage: ${formatNumberValue(values.efficientHeating, 0, 0)} t</li>
  `,
  transportChange_total: (values) => `
    <li>Geplante CO₂-Einsparung/Jahr: ${formatNumberValue(values.transportChange, 0, 0)} t</li>
    <li>Umstieg E-Auto: ${formatNumberValue(values.switchToElectricVehicle, 0, 0)} t</li>
    <li>Umstieg ÖPNV: ${formatNumberValue(values.switchToPublicTransport, 0, 0)} t</li>
    <li>Bidirektionales Laden: ${values.bidirectionalCharging === 0 ? '-' : `${formatNumberValue(values.bidirectionalCharging, 0, 0)} t`}</li>
    <li>Max. CO₂-Sparpotenzial: ${formatNumberValue(values.transportCo2SavingsPotential, 0, 0)} t</li>
  `,
  transportChange: (values) => `
    <li>Geplante CO₂-Einsparung/Jahr: ${formatNumberValue(values.transportChange, 0, 0)} t</li>
    <li>Umstieg E-Auto: ${formatNumberValue(values.switchToElectricVehicle, 0, 0)} t</li>
    <li>Umstieg ÖPNV: ${formatNumberValue(values.switchToPublicTransport, 0, 0)} t</li>
    <li>Bidirektionales Laden: ${values.bidirectionalCharging === 0 ? '-' : `${formatNumberValue(values.bidirectionalCharging, 0, 0)} t`}</li>
  `,
  consumptionChange_total: (values) => `
    <li>Geplante CO₂-Einsparung/Jahr: ${formatNumberValue(values.consumptionChange, 0, 0)} t</li>
    <li>Heiztemperatur senken: ${formatNumberValue(values.lowerHeatingTemperature, 0, 0)} t</li>
    <li>Smarte Thermostate: ${formatNumberValue(values.smartThermostat, 0, 0)} t</li>
    <li>Max. CO₂-Sparpotenzial: ${formatNumberValue(values.consumptionCo2SavingsPotential, 0, 0)} t</li>
  `,
  consumptionChange: (values) => `
    <li>Geplante CO₂-Einsparung/Jahr: ${formatNumberValue(values.consumptionChange, 0, 0)} t</li>
    <li>Heiztemperatur senken: ${formatNumberValue(values.lowerHeatingTemperature, 0, 0)} t</li>
    <li>Smarte Thermostate: ${formatNumberValue(values.smartThermostat, 0, 0)} t</li>
  `,
  flexScore: (values) => `
    <li>Ranking Gesamt: ${values.totalRanking}</li>
    <li>Flex-Score: ${values.flexScore}</li>
    <li>Durchschnitts-Platz: ${values.avgRanking}</li>
    <li>Platz Wärmepumpe: ${values.heatPumpRanking}</li>
    <li>Platz E-Autos: ${values.electricVehiclesRanking}</li>
    <li>Platz Heimspeicher: ${values.homeStorageRanking}</li>
    <li>Platz Mikro-Flexibilitäten: ${values.microFlexibilityRanking}</li>
    <li>Platz Flex-Wissen: ${values.flexKnowledgeRanking}</li>
    <li>Platz Flex-Bereitschaft: ${values.flexReadinessRanking}</li>
  `,
  technologyTotal: (values) => `
    <li>Flex-Menge/Haushalt Gesamt: ${formatNumberValue(values.totalKWhHousehold, 0, 0)} kWh</li>
    <li>Flex-Menge Gesamt: ${formatNumberValue(values.totalGWh, 0, 0)} GWh</li>
  `,
  technologyHeatpumps: (values) => `
    <li>Flex-Menge Wärmepumpe/Haushalt: ${formatNumberValue(values.heatPumpsKWhHousehold, 0, 0)} kWh</li>
    <li>Flex-Menge Wärmepumpe: ${formatNumberValue(values.heatPumpsGWh, 0, 0)} GWh</li>
    <li>Anzahl Wärmepumpen: ${formatNumberValue(values.heatPumpsTotal, 0, 0)}</li>
  `,
  technologyElectricVehicles: (values) => `
    <li>Flex-Menge E-Autos/Haushalt: ${formatNumberValue(values.electricVehiclesKWhHousehold, 0, 0)} kWh</li>
    <li>Flex-Menge E-Autos: ${formatNumberValue(values.electricVehiclesGWh, 0, 0)} GWh</li>
    <li>E-Autos: ${formatNumberValue(values.electricVehiclesTotal, 0, 0)}</li>
    ${values.electricVehiclesBidiReady != null ? `<li>davon bidirektionale E-Autos: ${formatNumberValue(values.electricVehiclesBidiReady, 0, 0)}</li>` : ''}
  `,
  financial: (values) => `
    <li>Gesamtersparnis: ${formatNumberValue(values.totalSavings, 1, 1)} Mio. €</li>
    <li>Gesamtersparnis durch Energielösungen: ${formatNumberValue(values.energySolutionsSavings, 1, 1)} Mio. €</li>
    <li>Gesamtersparnis durch Mikro-Flexibilitäten: ${formatNumberValue(values.microFlexibilitiesSavings, 1, 1)} Mio. €</li>
  `,
  technologyHomeStorage: (values) => `
    <li>Flex-Menge Heimspeicher/Haushalt: ${formatNumberValue(values.homeStorageKWhHousehold, 0, 0)} kWh</li>
    <li>Flex-Menge Heimspeicher: ${formatNumberValue(values.homeStorageGWh, 0, 0)} GWh</li>
    <li>Anzahl Heimspeicher: ${formatNumberValue(values.homeStorageTotal, 0, 0)}</li>
  `,
  technologyMicroFlexibilities: (values) => `
    <li>Flex-Menge Mikro-Flexibilitäten/Haushalt: ${formatNumberValue(values.microKWhHousehold, 0, 0)} kWh</li>
    <li>Flex-Menge Mikro-Flexibilitäten: ${formatNumberValue(values.microGWh, 0, 0)} GWh</li>
    <li>Anzahl Waschmaschinen: ${formatNumberValue(values.microWashingMachines, 0, 0)}</li>
    <li>Anzahl Spülmaschinen: ${formatNumberValue(values.microDishwasher, 0, 0)}</li>
    <li>Anzahl Trockner: ${formatNumberValue(values.microDryer, 0, 0)}</li>
  `,
  flexKnowledge: (values) => `<li>Flex-Wissen: ${values.flexKnowledge} %</li>`,
  flexReadiness: (values) => `<li>Flex-Bereitschaft: ${values.flexReadiness} %</li>`,
};

const MapDrawTooltipHelper = {
  createTooltipHtml: (tooltipKey, geoJson, tooltipContent, selectedYear, region) => {
    if (!tooltipContent || !tooltipContent.data?.length || !tooltipContent.data[0]) {
      return `
        <h3>${geoJson.properties.id}</h3>
        <ul>
          <li>Bisher keine Regional-Daten verfügbar oder Wert = 0,00</li>
        </ul>
      `;
    }

    const { name = geoJson.properties.id, values } = tooltipContent.data[0];

    if (name === 'Eisenach' && selectedYear >= 2021) {
      return `<p>Die zuvor kreisfreie Stadt Eisenach gehört<br />seit 1. Juli 2021 zum Wartburgkreis.</p>`;
    }

    const renderListItems = TOOLTIP_RENDERERS[tooltipKey];
    if (!renderListItems) {
      return `<h3>No matching Tooltip found</h3>`;
    }

    return `
      <h3>${name}</h3>
      <ul>${renderListItems(values, region)}</ul>
    `;
  }
};

export default MapDrawTooltipHelper;
